import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'
import { DAIMI_IS_SLUG } from '@/content/daimiInsurance'

/**
 * The Future 2030 Insurance outlook page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **The eighth Future 2030 outlook with a designed page of its own**, and the
 * seventeenth report overall. Asked for on 2026-08-28, in the same words as the
 * Automotive, Manufacturing, Consumer Goods, Retail, Pharmaceutical, Banking and
 * Services ones — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını
 * tasarla"* — as a four-part full-page capture of digitopia.co's Insurance outlook
 * page, plus the six images that page carries: the umbrella-on-a-circuit-board
 * cover art, the DMI forecast chart, the three requirement photographs and the
 * two-copy report mockup. And, as on the five requests before it, the instruction
 * that the maturity band point at the 2026 DAIMI report rather than the 2024 one
 * the live page offers.
 *
 * So the bands are the reference's, in the reference's order: hero → the forecast
 * beside Figure 1 → a dark band of three imperatives → the five predictions → the
 * pointer at the maturity report → the close. The two shared closing bands
 * (`content/reportClosing.ts`) still sit where they sit on every other report page.
 *
 * **Two things here are this page's own, and both are the reference's doing.**
 *
 *  * **The imperative cards are bulleted, and each ends in an outcome.** Every
 *    sibling's card is one sentence of prose; this one is three short lines and
 *    then "Outcome: higher loyalty and profitable growth." Flattening that into a
 *    paragraph would lose the promise the live card closes on, so `points` and
 *    `outcome` are new fields rather than a `body` with semicolons in it.
 *  * **The predictions band has no lead-in.** The Retail, Pharmaceutical, Banking
 *    and Services bands set the sector's state of play above the list; this one
 *    goes from the heading straight into the five. So there is no `intro` here.
 *
 * Everything else is the Services page's shape: a bold claim over a paragraph in
 * the hero, a dark band whose second line is a title rather than a second claim,
 * five plain statements rather than the Automotive page's label-and-figure pairs,
 * and the mockup beside them. Unlike Services, the forecast band *does* close on a
 * line of its own — `outro`, the way the Retail band does.
 *
 * **No sector slip in this reference.** The Pharmaceutical and Banking captures
 * both carry a maturity CTA reading "Explore the 2024 **Retail** Digital Maturity
 * Report" under a paragraph about their own sector. This page's CTA says Insurance,
 * so there was nothing to correct.
 *
 * **Two deliberate departures from the capture**, both the settled house call rather
 * than a reading of the reference:
 *
 *  * the h1 is the series headline every outlook page here uses ("Digital
 *    Transformation Outlook for Insurance") rather than the live page's "Future
 *    2030: Insurance Industry Outlook" — the eyebrow above it already says Future
 *    2030, and the eight outlook pages read as one series this way.
 *  * the maturity band carries the 2026 Insurance Industry Focus cover, which the
 *    live band does not. `OUTLOOK_IN_MATURITY.check` is where that is asked about,
 *    because it is the one place this page shows something the reference has not.
 *
 * **Where the words come from.** Everything here is transcribed from the four
 * captures, which are full-page renders read down to fit — the *shape* of the page
 * is certain and the *wording* is a careful reading of body copy set a handful of
 * pixels tall. Every band that carries prose therefore carries a `check`, the same
 * way `content/outlookServices.ts` does, and `outlookInsuranceChecks()` feeds them
 * into the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form — first and last name, business email,
 * company, job title, phone, country, industry, a marketing opt-in and the privacy
 * statement. These documents are prerendered with no client bundle at all
 * (`src/prerender.tsx`), so a form script would never run, and the other thirty
 * reports on this site hand the reader the PDF from our own bucket instead. Every
 * Download button here is that PDF, and `OUTLOOK_IN_CLOSE.check` is what keeps the
 * gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The short slug digitopia.co uses, and the one the request named. It was already in
 * `REPORTS` before this page existed, so the page is built under it as it stands
 * rather than moved to a `…-outlook-2030` one.
 */
export const OUTLOOK_IN_SLUG = 'insurance-industry-outlook'

/**
 * The Insurance Industry Focus cut of the Digital & AI Maturity Report 2026 — where
 * this page's maturity band sends the reader.
 *
 * It used to be written out here, because that report was one of the twenty drawn by
 * the shared template in `pages/Reports.tsx` and so had no content module of its own to
 * export a slug from. It got one hours later (`content/daimiInsurance.ts`, the
 * thirtieth designed report page), so this is that module's `DAIMI_IS_SLUG` under the
 * name this page already used — one literal for the two pages that name it, which is
 * one fewer place for them to drift apart.
 */
export const DAIMI_IN_SLUG = DAIMI_IS_SLUG

/**
 * The hero.
 *
 * `lede` is set bold above the paragraph because that is how the live hero sets it —
 * one claim in bold, the reason for it underneath — and the button says "Download
 * the Report" rather than the "Download Report" every other band uses, which is also
 * the live page's wording.
 */
export const OUTLOOK_IN_INTRO = {
  lede: 'What will winning insurers look like by 2030',
  body: 'The insurance industry is being reshaped by customer expectations, AI, data, and new entrants. See where leaders are heading, what changes next, and how to get ahead.',
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the umbrella standing on a glowing
   * circuit board, which is the artwork behind the report cover with none of its
   * lettering on it. This is where the live hero puts it too, at the same size and on
   * the same side.
   */
  photo: {
    src: '/photo/future-2030-insurance-digital-transformation.webp',
    alt: 'A blue umbrella standing open on a glowing circuit board',
  } satisfies Photo,
  check:
    'The hero’s two lines are transcribed from the reference capture rather than supplied as text. Read them back against digitopia.co before production — the bold claim has no full stop on the live page and has none here. The image, its side and its size are the live page’s.',
}

/**
 * The forecast band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average Insurers", "Laggards". Split into `label` and `body`
 * here so the label can be set rather than emphasised inside a sentence, which is the
 * call every designed report page on this site has made. Their openings are
 * capitalised as a result: the live page runs "Leaders: steady strength at 3.50…" as
 * one sentence, and a line of its own has to start like one.
 *
 * `outro` is the line the live band closes on before the button — unlike the Banking,
 * Pharmaceutical and Services bands, and like the Retail one.
 */
export const OUTLOOK_IN_FIGURE = {
  heading: 'Insurance Digital Maturity Forecast to 2030',
  intro: 'Digitopia’s five-year forecast reveals diverging futures:',
  points: [
    {
      label: 'Leaders',
      body: 'Steady strength at 3.50 in 2024. On an optimistic path they reach 4.12 by 2029, setting the pace for the market. Moderate 3.78. Pessimistic 3.59.',
    },
    {
      label: 'Average Insurers',
      body: 'From 2.99 in 2024 to 3.47 by 2029 in the optimistic case. Moderate 3.27. Pessimistic 3.07.',
    },
    {
      label: 'Laggards',
      body: 'At 1.90 in 2024. With decisive action they can climb to 2.85 by 2029. Without it they stall near 1.95.',
    },
  ],
  outro:
    'The divide widens by 2030. Leaders convert AI and customer centricity into dominance. Average firms must accelerate. Laggards face existential risk unless they reinvent now.',
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24)
   * and five of forecast (2025–29), the leader, average and laggard trajectories each
   * fanning out into an optimistic, moderate and pessimistic band. It is 1200×784 on
   * a white ground, so it gets a white card of its own and scales to it whole — ten
   * year columns with three labelled values each do get small on a phone, but a
   * figure the reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-insurance-dmi-forecast.webp',
    alt: 'Insurance DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Insurance Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'This is the most number-heavy of the eight outlook bands, and every figure in it was reconciled against Figure 1 rather than taken on trust: 3.50 / 2.99 / 1.90 are the chart’s own 2024 actuals, and 4.12–3.78–3.59, 3.47–3.27–3.07 and 2.85–1.95 are its 2029 optimistic, moderate and pessimistic labels. The page and the chart agree; the report still has to stand behind them. The wording is transcribed and wants a reading. One naming question, the same one the Banking and Services pages raised: this page calls the index DMI while every other page on this site calls it DAIMI, the Digital & AI Maturity Index. The chart itself is lettered DMI, so both were left as they are — say which name this page should use.',
}

/**
 * The dark imperatives band: two headline lines, then three cards.
 *
 * **The one band shaped unlike its seven siblings'.** Each card here is a short
 * bulleted list and then an outcome, rather than a sentence of prose — so `points`
 * carries the list and `outcome` the promise it closes on, both of them the live
 * card's own. The live band's second line is a title rather than a claim ("Three
 * Strategic Imperatives for 2030-Ready Insurance"), so it is set lighter and larger
 * under the headline, which is what the component already does with it.
 */
export const OUTLOOK_IN_REQUIREMENTS = {
  heading: 'Insurance Digital Revolution Has Arrived',
  subheading: 'Three Strategic Imperatives for 2030-Ready Insurance',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Digital, Customer-First',
      points: [
        'Personalize interactions across the lifecycle',
        'Remove friction from onboarding, policy, and claims',
        'Shift from reactive claims to proactive risk management',
      ],
      outcome: 'Outcome: higher loyalty and profitable growth.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/insurance-customer-first.webp',
        alt: 'A glowing shield and padlock projected above a device on a desk, connected to smaller security icons',
      } satisfies Photo,
    },
    {
      title: 'AI-First Operations',
      points: [
        'Automate underwriting, claims, and policy admin',
        'Use predictive analytics for risk, pricing, and fraud',
        'Deploy intelligent assistants for 24/7 service',
      ],
      outcome: 'Outcome: faster decisions, lower cost, better experiences.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/insurance-ai-first-operations.webp',
        alt: 'An analyst touching a large glowing padlock on a screen in a security operations room',
      } satisfies Photo,
    },
    {
      title: 'Upskill Your People',
      points: [
        'Build data, AI, and digital skills at scale',
        'Create a culture of experimentation and improvement',
        'Empower internal change champions',
      ],
      outcome: 'Outcome: a workforce that can execute the roadmap.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/insurance-upskilled-people.webp',
        alt: 'A smiling couple signing paperwork at a kitchen table with their adviser',
      } satisfies Photo,
    },
  ],
  check:
    'Two things. The two headline lines, the nine bullets and the three outcome lines are transcribed from the reference capture. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations. This is the same substitution the seven earlier outlook pages make, and one answer settles all eight.',
}

/**
 * The predictions band, with the report mockup beside it.
 *
 * **No lead-in.** The four bands before this one set the sector's state of play in a
 * paragraph or two above the list; the live Insurance band goes from the heading
 * straight into the five, so nothing was invented to fill the gap.
 *
 * The five are full sentences here rather than the clipped phrases the Services band
 * prints, because that is what the live band prints.
 */
export const OUTLOOK_IN_PREDICTIONS = {
  heading: 'Five Predictions for the 2030 Insurance Industry',
  items: [
    'Parametric insurance goes mainstream with instant, event-triggered payouts supported by IoT, satellite data, and blockchain.',
    'Cyber insurance surges into a core growth line as AI-enabled risk and protection become standard.',
    'Insurance becomes fully data-driven with real-time health, telematics, and wearables transforming pricing and claims.',
    'Digital self-service is the default across policy, service, and claims with humans focusing on complex cases.',
    'Open insurance ecosystems embed coverage into retail, auto, health, and finance journeys to unlock new revenue.',
  ],
  /** The mockup supplied with this request — the Future 2030 cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-insurance-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Insurance',
  } satisfies Photo,
  check:
    'The heading and all five predictions are transcribed from the reference capture. None of the five carries a figure, so there is nothing here to reconcile against Figure 1 — only wording. The mockup beside them is the file you supplied; the reference capture shows that half of the band empty, so say if the live page really carries no image there and it should come out. This is the same call the Retail, Banking, Pharmaceutical and Services pages made.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a
 * picture.** digitopia.co sends the reader to a *2024* report; the instruction with
 * the captures was *"en aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI
 * report'a yönlendirelim"*. So the CTA, the cover and the sentence all name the
 * Insurance Industry Focus cut of the Digital & AI Maturity Report 2026, which is the
 * report this site actually publishes.
 *
 * That is settled, so it is not what `check` asks about: the same swap was left open
 * on the Automotive page and has now been answered eight times.
 */
export const OUTLOOK_IN_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The Insurance Industry Focus cut of the Digital & AI Maturity Report 2026 reveals where companies stand today, and where they need to go. Compare your performance with leaders, average performers, and laggards. Are you leading the shift or reacting to it? Use our data to compare your performance, assess your risks, and find the fastest route to transformation.',
  ctaLabel: 'Explore the Insurance Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_IN_SLUG}`,
  /** That report's cover, which this site already holds. */
  photo: {
    src: '/photo/daimi-report-2026-insurance-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Insurance Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the “2024 Insurance Digital Maturity Report” the live page offers, which is what you asked for — so the CTA label and the sentence naming the report are the 2026 cut’s. Three things left to decide. The rest of the paragraph is the live one, so it wants a reading. The heading is the one every outlook page here uses; the live one runs two headings together — “How Does Your Digital Maturity Compare to Industry Leaders? Benchmark Your Digital Maturity” — which reads as a template left half-filled, so say if the longer form is really wanted. And the cover beside it is not on the live page at all: it is here because the other seven Future 2030 outlook pages show the maturity cut they point at, and it should come out if this band is meant to stay text-only.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * Its heading and its sentence are the live band's own — "Start Building Your 2030
 * Advantage", which is an invitation rather than the "Download the Full Report" the
 * Pharmaceutical, Banking and Services pages close with, and the same shape the
 * Retail page's close has.
 */
export const OUTLOOK_IN_CLOSE = {
  heading: 'Start Building Your 2030 Advantage',
  body: 'Get the full Future 2030 Insurance Outlook with maturity forecasts, strategic priorities, and practical next steps to move faster with confidence.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `outlookServicesChecks()` uses, and appended to the same
 * list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page
 * arrived with all six of its images. What is left is transcription nobody has read
 * against the original, one destination this site cannot offer, one image this page
 * shows that the reference does not, and the gating decision.
 */
export function outlookInsuranceChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_IN_INTRO.check },
    { section: 'outlook', check: OUTLOOK_IN_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_IN_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_IN_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_IN_MATURITY.check },
    { section: 'download', check: OUTLOOK_IN_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_IN_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
